import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
  roles,
  permissions,
  menus,
  roleMenus,
  type RoleItem,
  type PermissionItem,
  type MenuItem,
} from "../../../../services/admin";
import { ensurePermission } from "../../../../utils/permissions";
import RIcon from "../../../../components/RIcon";

const RESOURCE_LABEL: Record<string, string> = {
  users: "用户管理",
  roles: "角色管理",
  permissions: "权限管理",
  menus: "菜单管理",
  departments: "部门管理",
  files: "文件管理",
  projects: "项目中心",
  training: "模型训练",
  data_permissions: "数据权限管理",
  settings: "系统设置",
  subscriptions: "内容订阅",
  job_types: "工种管理",
  work_stages: "研发阶段管理",
  chat: "AI 大管家",
  doc_gen: "智能文档生成",
};

const ACTION_LABEL: Record<string, string> = {
  read: "查看",
  write: "编辑/新增",
  delete: "删除",
  download: "下载",
};

function translatePermission(perm: PermissionItem): string {
  if (perm.description && /[一-鿿]/.test(perm.description)) {
    // 后端 description 已是中文，直接拼接界面前缀
    const label = RESOURCE_LABEL[perm.resource] || perm.resource;
    return `${label}：${perm.description}`;
  }
  const resource = RESOURCE_LABEL[perm.resource] || perm.resource;
  const action = ACTION_LABEL[perm.action] || perm.action;
  return `${resource}：${action}`;
}

export default function RolesPage() {
  const { user } = useOutletContext<{ user: import("../../../../services/auth").UserInfo | null }>();
  const [roleList, setRoleList] = useState<RoleItem[]>([]);
  const [allPerms, setAllPerms] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  // Config modal state
  const [configRole, setConfigRole] = useState<RoleItem | null>(null);
  const [rolePermIds, setRolePermIds] = useState<number[]>([]);
  const [originalPermIds, setOriginalPermIds] = useState<number[]>([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  // Menu config modal state
  const [menuConfigRole, setMenuConfigRole] = useState<RoleItem | null>(null);
  const [allMenus, setAllMenus] = useState<MenuItem[]>([]);
  const [roleMenuIds, setRoleMenuIds] = useState<number[]>([]);
  const [originalMenuIds, setOriginalMenuIds] = useState<number[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuSaving, setMenuSaving] = useState(false);

  // Create modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [createSaving, setCreateSaving] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadRoles = async () => {
    try {
      const data = await roles.list();
      setRoleList(data);
    } catch {
      showToast("加载角色列表失败");
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [rolesData, permsData] = await Promise.all([
          roles.list(),
          permissions.list(),
        ]);
        setRoleList(rolesData);
        setAllPerms(permsData);
      } catch {
        showToast("加载数据失败");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleOpenConfig = async (role: RoleItem) => {
    if (!ensurePermission(user, "roles:write", showToast)) return;
    setConfigRole(role);
    setPermLoading(true);
    try {
      const ids = await permissions.getForRole(role.id);
      setRolePermIds(ids);
      setOriginalPermIds(ids);
    } catch {
      showToast("加载角色权限失败");
      setRolePermIds([]);
      setOriginalPermIds([]);
    } finally {
      setPermLoading(false);
    }
  };

  const handleTogglePerm = (permId: number) => {
    setRolePermIds((prev) =>
      prev.includes(permId)
        ? prev.filter((id) => id !== permId)
        : [...prev, permId]
    );
  };

  const handleSelectAll = () => {
    const allIds = allPerms.map((p) => p.id);
    setRolePermIds((prev) =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleSavePerms = async () => {
    if (!ensurePermission(user, "roles:write", showToast)) return;
    if (!configRole) return;
    setPermSaving(true);
    try {
      const toAdd = rolePermIds.filter((id) => !originalPermIds.includes(id));
      const toRemove = originalPermIds.filter((id) => !rolePermIds.includes(id));

      await Promise.all([
        ...toAdd.map((permId) => permissions.addToRole(configRole.id, permId)),
        ...toRemove.map((permId) => permissions.removeFromRole(configRole.id, permId)),
      ]);

      setConfigRole(null);
      showToast("权限配置已保存");
    } catch {
      showToast("保存权限配置失败");
    } finally {
      setPermSaving(false);
    }
  };

  const handleOpenMenuConfig = async (role: RoleItem) => {
    if (!ensurePermission(user, "roles:write", showToast)) return;
    setMenuConfigRole(role);
    setMenuLoading(true);
    try {
      const [ids, menuItems] = await Promise.all([
        roleMenus.get(role.id),
        allMenus.length > 0 ? Promise.resolve(allMenus) : menus.list(),
      ]);
      setAllMenus(menuItems);
      setRoleMenuIds(ids);
      setOriginalMenuIds(ids);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "加载角色菜单失败");
      setRoleMenuIds([]);
      setOriginalMenuIds([]);
    } finally {
      setMenuLoading(false);
    }
  };

  const handleToggleMenu = (menuId: number) => {
    setRoleMenuIds((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleSelectAllMenus = () => {
    const allIds = allMenus.map((m) => m.id);
    setRoleMenuIds((prev) =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleSaveMenus = async () => {
    if (!ensurePermission(user, "roles:write", showToast)) return;
    if (!menuConfigRole) return;
    setMenuSaving(true);
    try {
      await roleMenus.set(menuConfigRole.id, roleMenuIds);
      setMenuConfigRole(null);
      showToast("菜单配置已保存");
    } catch {
      showToast("保存菜单配置失败");
    } finally {
      setMenuSaving(false);
    }
  };

  // 默认菜单：基础功能（AI大管家、文件管理、项目中心）
  const DEFAULT_MENU_NAMES = ["AI 大管家", "文件管理", "项目中心", "智能文档生成"];

  const handleCreate = async () => {
    if (!ensurePermission(user, "roles:write", showToast)) return;
    if (!createForm.name.trim()) {
      showToast("请输入角色名称");
      return;
    }
    setCreateSaving(true);
    try {
      const result = await roles.create({
        name: createForm.name,
        description: createForm.description || undefined,
      });
      // 自动分配默认菜单
      const menuItems = allMenus.length > 0 ? allMenus : await menus.list();
      setAllMenus(menuItems);
      const defaultMenuIds = menuItems
        .filter((m) => DEFAULT_MENU_NAMES.includes(m.name))
        .map((m) => m.id);
      if (defaultMenuIds.length > 0 && result.id) {
        try {
          await roleMenus.set(result.id, defaultMenuIds);
        } catch { /* ignore */ }
      }
      setCreateOpen(false);
      setCreateForm({ name: "", description: "" });
      await loadRoles();
      showToast("角色创建成功（已分配默认菜单）");
    } catch {
      showToast("创建角色失败");
    } finally {
      setCreateSaving(false);
    }
  };

  const handleDeleteRole = async (role: RoleItem) => {
    if (!ensurePermission(user, "roles:delete", showToast)) return;
    if (role.is_system) {
      showToast("系统角色不可删除");
      return;
    }
    try {
      await roles.delete(role.id);
      await loadRoles();
      showToast("角色已删除");
    } catch {
      showToast("删除角色失败");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RIcon name="loader-4-line" className="animate-spin text-2xl text-[#0EA5A0]" />
        <span className="ml-2 text-[#718096]">加载中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2B3C]">角色管理</h2>
          <p className="text-xs text-[#718096] mt-0.5">配置系统角色及其数据权限范围</p>
        </div>
        <button
          onClick={() => ensurePermission(user, "roles:write", showToast) && setCreateOpen(true)}
          className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] text-white rounded-lg text-sm font-medium hover:bg-[#0D9590] cursor-pointer whitespace-nowrap"
        >
          <RIcon name="add-line" />新建角色
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                {["角色名称", "用户数", "描述", "类型", "操作"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roleList.map((role) => (
                <tr key={role.id} className="border-b border-gray-50 hover:bg-[#F4F8FB]/50 transition-colors">
                  <td className="px-4 py-3 font-semibold text-[#1A2B3C] whitespace-nowrap">{role.name}</td>
                  <td className="px-4 py-3 text-[#4A5568]">{role.user_count} 人</td>
                  <td className="px-4 py-3 text-[#718096] text-xs max-w-48">
                    <span className="truncate block">{role.description || "-"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${role.is_system ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"}`}>
                      {role.is_system ? "系统角色" : "自定义"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenConfig(role)}
                        className="flex items-center gap-1 px-2.5 h-7 bg-[#F4F8FB] border border-gray-200 text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] rounded-lg text-xs transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <RIcon name="settings-3-line" />配置权限
                      </button>
                      <button
                        onClick={() => handleOpenMenuConfig(role)}
                        className="flex items-center gap-1 px-2.5 h-7 bg-[#F4F8FB] border border-gray-200 text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] rounded-lg text-xs transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <RIcon name="menu-line" />分配菜单
                      </button>
                      {!role.is_system && (
                        <button
                          onClick={() => handleDeleteRole(role)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 cursor-pointer"
                        >
                          <RIcon name="delete-bin-line" className="text-sm" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {roleList.length === 0 && (
            <div className="text-center py-10 text-[#718096] text-sm">暂无角色数据</div>
          )}
        </div>
      </div>

      {/* Config Modal */}
      {configRole && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-[#1A2B3C]">配置权限</h3>
                <p className="text-xs text-[#718096]">{configRole.name}</p>
              </div>
              <button onClick={() => setConfigRole(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {permLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RIcon name="loader-4-line" className="animate-spin text-xl text-[#0EA5A0]" />
                  <span className="ml-2 text-[#718096] text-sm">加载权限...</span>
                </div>
              ) : allPerms.length === 0 ? (
                <div className="text-center py-8 text-[#718096] text-sm">暂无可用权限</div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-[#1A2B3C]">权限模块</p>
                    <button
                      onClick={handleSelectAll}
                      className="text-xs text-[#0EA5A0] hover:underline cursor-pointer"
                    >
                      {rolePermIds.length === allPerms.length ? "取消全选" : "全选"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-6 max-h-64 overflow-y-auto">
                    {allPerms.map((perm) => (
                      <label key={perm.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          className="accent-[#0EA5A0]"
                          checked={rolePermIds.includes(perm.id)}
                          onChange={() => handleTogglePerm(perm.id)}
                        />
                        <span className="text-sm text-[#4A5568]">{translatePermission(perm)}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              <div className="flex gap-3">
                <button onClick={() => setConfigRole(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button
                  onClick={handleSavePerms}
                  disabled={permSaving || permLoading}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >{permSaving ? <><RIcon name="loader-4-line" className="animate-spin" />保存中...</> : "保存配置"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">新建角色</h3>
              <button onClick={() => setCreateOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">角色名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="如：部门主管"
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">描述</label>
                <textarea
                  rows={3}
                  placeholder="角色描述..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0EA5A0] resize-none"
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setCreateOpen(false)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button
                  onClick={handleCreate}
                  disabled={createSaving}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >{createSaving ? <><RIcon name="loader-4-line" className="animate-spin" />创建中...</> : "创建角色"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Config Modal */}
      {menuConfigRole && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-[#1A2B3C]">分配菜单</h3>
                <p className="text-xs text-[#718096]">{menuConfigRole.name}</p>
              </div>
              <button onClick={() => setMenuConfigRole(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {menuLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RIcon name="loader-4-line" className="animate-spin text-xl text-[#0EA5A0]" />
                  <span className="ml-2 text-[#718096] text-sm">加载菜单...</span>
                </div>
              ) : allMenus.length === 0 ? (
                <div className="text-center py-8 text-[#718096] text-sm">暂无可用菜单</div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-[#1A2B3C]">菜单列表</p>
                    <button
                      onClick={handleSelectAllMenus}
                      className="text-xs text-[#0EA5A0] hover:underline cursor-pointer"
                    >
                      {roleMenuIds.length === allMenus.length ? "取消全选" : "全选"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-6 max-h-64 overflow-y-auto">
                    {allMenus.map((menu) => (
                      <label key={menu.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          className="accent-[#0EA5A0]"
                          checked={roleMenuIds.includes(menu.id)}
                          onChange={() => handleToggleMenu(menu.id)}
                        />
                        <div className="flex items-center gap-1.5 min-w-0">
                          {menu.icon && <i className={`${menu.icon} text-sm text-[#718096]`} />}
                          <span className="text-sm text-[#4A5568] truncate">{menu.name}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
              <div className="flex gap-3">
                <button onClick={() => setMenuConfigRole(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button
                  onClick={handleSaveMenus}
                  disabled={menuSaving || menuLoading}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >{menuSaving ? <><RIcon name="loader-4-line" className="animate-spin" />保存中...</> : "保存配置"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
